<template>
  <paper-modal-container :is-visible="isVisible" :title="modalTitle" :hide-close-button="false" @close="cancelPopup">
    <br />

    <!-- Step Indicator -->
    <div :class="$style.stepIndicator">
      <div v-for="step in 5" :key="step"
        :class="[$style.stepItem, { [$style.active]: currentStep === step, [$style.completed]: currentStep > step }]">
        <div :class="$style.stepNumber">{{ step }}</div>
        <div :class="$style.stepLabel">{{ getStepLabel(step) }}</div>
      </div>
    </div>

    <!-- Step 1: 기본 정보 -->
    <div v-if="currentStep === 1">

      <div :class="$style.input">
        <h4 :class="$style.required">커미션명</h4>
        <input :class="$style.textInput" v-model="name" placeholder="커미션명 입력" :disabled="uploading" />
      </div>
      <div :class="$style.input">
        <h4 :class="$style.required">커미션 타입</h4>
        <div :class="$style.typeSelector">
          <button v-for="opt in categoryOptions" :key="opt.value" type="button" :class="[
            $style.typeButton,
            { [$style.typeButtonActive]: category === opt.value },
          ]" :disabled="uploading" @click="category = category === opt.value ? '' : opt.value">
            <span :class="$style.typeLabel">{{ opt.label }}</span>
          </button>
        </div>
      </div>


      <div :class="$style.input">
        <h4 :class="$style.required">썸네일</h4>
        <div v-if="thumbnailPreviewUrl" :class="$style.imagePreviewContainer">
          <div :class="$style.imagePreview">
            <img :src="thumbnailPreviewUrl" alt="썸네일" :class="$style.previewImage" />
          </div>
        </div>
        <label :class="$style.fileInputLabel" :for="'thumbnailFile-' + formUid">
          <input ref="thumbnailFileInput" :id="'thumbnailFile-' + formUid" :class="$style.fileInputHidden" type="file"
            @change="handleThumbnailUpload" :disabled="uploading" accept="image/*" />
          <span :class="$style.fileInputButton">
            <span v-if="!thumbnailFile">📷 파일 선택</span>
            <span v-else>📄 {{ thumbnailFile.name }}</span>
          </span>
        </label>
        <div v-if="thumbnailFile" :class="$style.newFileInfo">새 파일: {{ thumbnailFile.name }}</div>
      </div>

      <div :class="$style.input">
        <h4>해시태그 <span>(선택, 최대 {{ maxHashtags }}개)</span></h4>
        <div :class="$style.hashtagInputRow">
          <input :class="$style.textInput" v-model="hashtagInput" placeholder="태그 입력 후 Enter 또는 쉼표로 추가"
            :disabled="uploading || hashtags.length >= maxHashtags" @keydown.enter.prevent="addHashtag"
            @keydown="handleHashtagKeydown" />
          <button type="button" :class="$style.addButton" @click="addHashtag"
            :disabled="uploading || !hashtagInput.trim() || hashtags.length >= maxHashtags">
            추가
          </button>
        </div>
        <div v-if="hashtags.length > 0" :class="$style.hashtagList">
          <span v-for="(tag, idx) in hashtags" :key="tag + idx" :class="$style.hashtagChip">
            <span :class="$style.hashtagText">#{{ tag }}</span>
            <button type="button" :class="$style.hashtagRemove" @click="removeHashtag(idx)" :disabled="uploading"
              aria-label="태그 삭제">×</button>
          </span>
        </div>
      </div>
    </div>

    <!-- Step 2: 세부 이미지 -->
    <div v-if="currentStep === 2">
      <div v-for="(img, idx) in detailImages" :key="idx" :class="$style.input">
        <h4>세부 이미지 {{ idx + 1 }} <span>(선택)</span></h4>
        <div v-if="img.previewUrl" :class="$style.imagePreviewContainer">
          <div :class="$style.imagePreview">
            <img :src="img.previewUrl" :alt="'세부 이미지 ' + (idx + 1)" :class="$style.previewImage" />
            <button v-if="isUpdateMode && !img.file" :class="$style.deleteImageButton" @click="deleteDetailImage(idx)"
              :disabled="uploading">
              삭제
            </button>
          </div>
        </div>
        <label :class="$style.fileInputLabel" :for="'detailImage-' + formUid + '-' + idx">
          <input :ref="'detailImageInput' + idx" :id="'detailImage-' + formUid + '-' + idx"
            :class="$style.fileInputHidden" type="file" @change="handleDetailImageUpload($event, idx)"
            :disabled="uploading" accept="image/*" />
          <span :class="$style.fileInputButton">
            <span v-if="!img.file">📷 파일 선택</span>
            <span v-else>📄 {{ img.file.name }}</span>
          </span>
        </label>
        <div v-if="img.file" :class="$style.newFileInfo">새 파일: {{ img.file.name }}</div>
      </div>
    </div>

    <!-- Step 3: 상세 정보 (TinyMCE — HTML) -->
    <div v-if="currentStep === 3">
      <!-- Toast UI 에디터(마크다운) — 보관용. TinyMCE를 메인으로 사용 중이라 숨김 -->
      <div v-if="false" :class="$style.input">
        <h4>설명 (Toast UI · 마크다운)</h4>
        <div ref="descriptionEditorMount" :class="$style.descriptionEditorHost" />
      </div>

      <div :class="$style.input">
        <h4>설명 <span>(선택)</span></h4>
        <div :class="$style.tinyEditorHost">
          <TinyEditor v-model="descriptionHtml" :init="tinyEditorInit" :disabled="uploading" />
        </div>
      </div>
    </div>

    <!-- Step 4: 영상 등록 -->
    <div v-if="currentStep === 4">
      <div :class="$style.input">
        <h4>유튜브 영상 <span>(선택)</span></h4>
        <div :class="$style.youtubeInputRow">
          <input :class="$style.textInput" v-model="youtubeInput" placeholder="YouTube URL을 붙여넣으세요"
            :disabled="uploading || youtubeAdding" @keydown.enter.prevent="addYoutubeVideo" />
          <button type="button" :class="$style.addButton" @click="addYoutubeVideo"
            :disabled="uploading || youtubeAdding || !youtubeInput.trim()">
            <span v-if="youtubeAdding">...</span>
            <span v-else>추가</span>
          </button>
        </div>
        <div v-if="youtubeVideos.length > 0" :class="$style.youtubeVideoList">
          <div v-for="(video, idx) in youtubeVideos" :key="video.vid" :class="$style.youtubeVideoItem">
            <img :src="`https://img.youtube.com/vi/${video.vid}/mqdefault.jpg`" :class="$style.youtubeVideoThumb"
              :alt="video.title" />
            <div :class="$style.youtubeVideoInfo">
              <div :class="$style.youtubeVideoTitle">{{ video.title }}</div>
              <div :class="$style.youtubeVideoChannel">{{ video.channel }}</div>
            </div>
            <button type="button" :class="$style.removeButton" @click="removeYoutubeVideo(idx)" :disabled="uploading">
              삭제
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 5: 구매 방식 -->
    <div v-if="currentStep === 5">
      <div :class="$style.input">
        <h4 :class="$style.required">구매 방식</h4>
        <div :class="$style.radio">
          <label>
            <input type="radio" value="link" v-model="purchaseType" :disabled="uploading" />
            외부 링크
          </label>
          <label>
            <input type="radio" value="linkook" v-model="purchaseType"
              :disabled="uploading || userStore.auth !== 'admin'" />
            링꾹 구매
          </label>
        </div>
      </div>

      <div :class="$style.input">
        <h4 :class="$style.required">구매가격 <span>(기본가)</span></h4>
        <input :class="$style.textInput" v-model.number="basePrice" type="number" min="0" placeholder="예) 5,000원"
          :disabled="uploading" />
      </div>

      <div v-if="purchaseType === 'link'" :class="$style.input">
        <h4 :class="$style.required">구매 링크 <span>(URL)</span></h4>
        <input :class="$style.textInput" v-model="purchaseLink" placeholder="https://example.com/order"
          :disabled="uploading" />
      </div>

      <template v-if="purchaseType === 'linkook'">
        <div :class="$style.input">
          <h4 :class="$style.required">정산 은행 <span style="font-size: 12px"></span></h4>
          <input :class="$style.textInput" v-model="settlementBank" placeholder="카카오뱅크" :disabled="uploading" />
        </div>
        <div :class="$style.input">
          <h4 :class="$style.required">정산 계좌번호 <span style="font-size: 12px"></span></h4>
          <input :class="$style.textInput" v-model="settlementAccountNumber" placeholder="3333-01-1234567"
            :disabled="uploading" />
        </div>
        <div :class="$style.input">
          <h4 :class="$style.required">정산 예금주 <span style="font-size: 12px"></span></h4>
          <input :class="$style.textInput" v-model="settlementAccountHolder" placeholder="홍길동" :disabled="uploading" />
        </div>
        <div :class="$style.input">
          <h4 :class="$style.required">판매자명</h4>
          <input :class="$style.textInput" v-model="sellerName" placeholder="판매자명 입력" :disabled="uploading" />
        </div>
        <div :class="$style.input">
          <p :class="$style.infoText">
            💡 예매 폼 질문 설정은 공연 등록 후 공연 상세 화면에서 가능합니다.
          </p>
        </div>
      </template>
    </div>

    <!-- 버튼 영역 -->
    <div :class="$style.buttons">
      <button :class="$style.cancel" @click="cancelPopup" :disabled="uploading">
        취소
      </button>
      <button v-if="currentStep > 1" :class="$style.prevButton" @click="prevStep" :disabled="uploading">
        이전
      </button>
      <button v-if="showSaveDraftButton" :class="$style.saveDraft" @click="handleSaveDraft"
        :disabled="uploading || !canSaveDraft">
        <span v-if="!uploading">임시저장</span>
        <span v-else> <i :class="$style.spinner"></i> {{ loadingText }}</span>
      </button>
      <button v-if="currentStep < 5" :class="$style.nextButton" @click="nextStep" :disabled="uploading">
        다음
      </button>
      <button v-if="currentStep === 5" :class="$style.register" @click="handleSubmit" :disabled="uploading">
        <span v-if="!uploading">{{ submitButtonText }}</span>
        <span v-else> <i :class="$style.spinner"></i> {{ loadingText }}</span>
      </button>
    </div>
  </paper-modal-container>
</template>

<script>
import { ref } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { storage } from "/firebaseConfig";
import { createCommission, updateCommission } from "@/utils/write/commission";
import { createToast } from "mosha-vue-toastify";
import { useUserStore } from "@/store/user";
import "mosha-vue-toastify/dist/style.css";
import PaperModalContainer from "@/components/modal/PaperModalContainer.vue";
import axios from "axios";
import Editor from "@toast-ui/editor";
import "@toast-ui/editor/dist/toastui-editor.css";
import "@toast-ui/editor/dist/i18n/ko-kr";
import colorSyntax from "@toast-ui/editor-plugin-color-syntax";
import "tui-color-picker/dist/tui-color-picker.css";
import "@toast-ui/editor-plugin-color-syntax/dist/toastui-editor-plugin-color-syntax.css";
import tableMergedCell from "@toast-ui/editor-plugin-table-merged-cell";
import "@toast-ui/editor-plugin-table-merged-cell/dist/toastui-editor-plugin-table-merged-cell.css";
import { uploadToastImage, prepareToastViewerMarkdown } from "@/utils/write/toastui";
// TinyMCE (self-hosted v6) — 메인 에디터로 사용
import TinyEditor from "@tinymce/tinymce-vue";
import "tinymce/tinymce";
import "tinymce/models/dom/model";
import "tinymce/themes/silver";
import "tinymce/icons/default";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/plugins/link";
import "tinymce/plugins/image";
import "tinymce/plugins/table";
import "tinymce/plugins/lists";
import "tinymce/plugins/media";
import "tinymce/plugins/code";
import "tinymce/plugins/autolink";
import "tinymce-i18n/langs6/ko_KR";

let _formUidCounter = 0;

export default {
  name: "CommissionForm",
  components: {
    PaperModalContainer,
    TinyEditor,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "create",
      validator: (value) => ["create", "update"].includes(value),
    },
    commissionData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      formUid: `cf_${++_formUidCounter}`,
      userStore: useUserStore(),
      currentStep: 1,
      // Step 1
      category: "",
      categoryOptions: [
        { label: "디자인", value: "design" },
        { label: "영상", value: "video" },
        { label: "음악", value: "music" },
        { label: "기타", value: "etc" },
        { label: "일러스트", value: "illustration" },
      ],
      name: "",
      thumbnailFile: null,
      originalThumbnail: null,
      thumbnailPreviewUrl: null,
      hashtags: [],
      hashtagInput: "",
      maxHashtags: 10,
      // Step 2
      detailImages: [
        { file: null, original: null, previewUrl: null },
        { file: null, original: null, previewUrl: null },
        { file: null, original: null, previewUrl: null },
        { file: null, original: null, previewUrl: null },
      ],
      // Step 3
      link: "",
      description: "",
      descriptionHtml: "",
      // Step 4
      youtubeVideos: [],
      youtubeInput: "",
      youtubeAdding: false,
      // Step 5
      purchaseType: "link",
      basePrice: null,
      purchaseLink: "",
      settlementBank: "",
      settlementAccountNumber: "",
      settlementAccountHolder: "",
      sellerName: "",
      // Common
      uploading: false,
      /** Firestore 상태: draft(임시저장) / published(게시) — LiveForm과 동일 패턴 */
      status: "published",
      /** populate 중 purchaseType watcher가 링꾹 구매를 되돌리지 않도록 */
      ignorePurchaseTypeWatch: false,
    };
  },
  computed: {
    isUpdateMode() {
      return this.mode === "update";
    },
    modalTitle() {
      return this.isUpdateMode ? "커미션 수정" : "커미션 등록";
    },
    submitButtonText() {
      if (this.isUpdateMode && this.status !== "draft") return "수정";
      return "등록";
    },
    loadingText() {
      return "";
    },
    showSaveDraftButton() {
      if (this.isUpdateMode && this.status !== "draft") return false;
      return true;
    },
    tinyEditorInit() {
      const self = this;
      return {
        height: 480,
        menubar: false,
        skin: false,
        content_css: false,
        language: "ko_KR",
        branding: false,
        promotion: false,
        elementpath: false,
        plugins: "lists link image table media code autolink",
        toolbar:
          "undo redo | blocks | bold italic underline strikethrough | forecolor backcolor | " +
          "alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | " +
          "link image media table | removeformat code",
        default_link_target: "_blank",
        link_default_target: "_blank",
        link_assume_external_targets: true,
        link_target_list: [
          { title: "새 창", value: "_blank" },
          { title: "현재 창", value: "_self" },
        ],
        relative_urls: false,
        convert_urls: false,
        media_live_embeds: true,
        content_style:
          "body{font-family:Pretendard,'NanumSquareRoundR',sans-serif;font-size:14px;line-height:1.6;color:#212529;padding:8px 4px;}" +
          "p{margin:0 0 8px 0;}img{max-width:100%;height:auto;border-radius:6px;}",
        images_upload_handler: (blobInfo) =>
          new Promise((resolve, reject) => {
            const blob = blobInfo.blob();
            const filename = blobInfo.filename();
            if (filename && !blob.name) {
              try {
                Object.defineProperty(blob, "name", {
                  value: filename,
                  configurable: true,
                });
              } catch (_) {
                /* readonly fallback */
              }
            }
            uploadToastImage(blob, "tinymce")
              .then((url) => resolve(url))
              .catch((err) => {
                console.error("TinyMCE 이미지 업로드 실패:", err);
                self.toast("이미지 업로드에 실패했습니다", "danger");
                reject({ message: "이미지 업로드에 실패했습니다", remove: true });
              });
          }),
        file_picker_types: "image",
      };
    },
    canSaveDraft() {
      if (this.isUpdateMode) return true;
      if (!this.category) return false;
      if (!this.name.trim()) return false;
      if (!this.thumbnailFile && !this.originalThumbnail) return false;
      if (this.link.trim() && !this.checkUrl(this.link.trim())) return false;
      if (!this.isValidBasePrice(this.basePrice)) return false;
      if (this.purchaseType === "linkook") {
        if (this.userStore.auth !== "admin") return false;
        if (!this.settlementBank.trim()) return false;
        if (!this.settlementAccountNumber.trim()) return false;
        if (!this.settlementAccountHolder.trim()) return false;
        if (!this.sellerName.trim()) return false;
      }
      return true;
    },
  },
  beforeUnmount() {
    this.destroyDescriptionEditor();
  },
  watch: {
    commissionData: {
      immediate: true,
      async handler(newData) {
        if (newData && this.isUpdateMode) {
          await this.populateForm(newData);
        }
      },
    },
    isVisible: {
      handler(val) {
        if (!val) {
          this.destroyDescriptionEditor();
        }
        if (val && !this.isUpdateMode) {
          this.renew();
        }
        if (val && this.currentStep === 3) {
          this.$nextTick(() => this.initDescriptionEditor());
        }
      },
      flush: "pre",
    },
    currentStep(val, prev) {
      if (prev === 3 && val !== 3) {
        this.destroyDescriptionEditor();
      }
      if (val === 3) {
        this.$nextTick(() => this.initDescriptionEditor());
      }
    },
    purchaseType(newVal) {
      if (this.ignorePurchaseTypeWatch) return;
      if (newVal === "linkook" && this.userStore.auth !== "admin") {
        this.$alert("관리자에게 문의해주세요");
        this.$nextTick(() => {
          this.purchaseType = "link";
        });
      }
    },
  },
  methods: {
    getStepLabel(step) {
      const labels = {
        1: "기본 정보",
        2: "세부 이미지",
        3: "상세 정보",
        4: "영상 등록",
        5: "구매 방식",
      };
      return labels[step] || "";
    },
    nextStep() {
      if (this.currentStep < 5) this.currentStep++;
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep--;
    },
    // Step 1 handlers
    handleThumbnailUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.thumbnailFile = file;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.thumbnailPreviewUrl = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    // Step 1 handlers — 해시태그
    addHashtag() {
      const raw = (this.hashtagInput || "").trim().replace(/^#+/, "");
      if (!raw) return;
      if (this.hashtags.length >= this.maxHashtags) return;
      const normalized = raw.replace(/\s+/g, "");
      if (!normalized) return;
      if (normalized.length > 20) {
        this.toast("태그는 20자 이내로 입력해주세요", "default");
        return;
      }
      if (this.hashtags.some(t => t.toLowerCase() === normalized.toLowerCase())) {
        this.toast("이미 추가된 태그입니다", "default");
        this.hashtagInput = "";
        return;
      }
      this.hashtags.push(normalized);
      this.hashtagInput = "";
    },
    removeHashtag(idx) {
      this.hashtags.splice(idx, 1);
    },
    handleHashtagKeydown(event) {
      if (event.key === "," || event.key === " ") {
        event.preventDefault();
        this.addHashtag();
        return;
      }
      if (event.key === "Backspace" && !this.hashtagInput && this.hashtags.length > 0) {
        this.hashtags.pop();
      }
    },

    // Step 2 handlers
    handleDetailImageUpload(event, idx) {
      const file = event.target.files[0];
      if (file) {
        this.detailImages[idx].file = file;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.detailImages[idx].previewUrl = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    deleteDetailImage(idx) {
      this.detailImages[idx].original = null;
      this.detailImages[idx].previewUrl = null;
      this.detailImages[idx].file = null;
    },

    // Step 4 handlers
    extractYoutubeId(url) {
      const pattern = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
      const match = url.match(pattern);
      return match ? match[1] : null;
    },
    async addYoutubeVideo() {
      const url = this.youtubeInput.trim();
      if (!url) return;
      const vid = this.extractYoutubeId(url);
      if (!vid) {
        this.toast("올바른 YouTube URL을 입력해주세요.", "default");
        return;
      }
      if (this.youtubeVideos.some(v => v.vid === vid)) {
        this.toast("이미 추가된 영상입니다.", "default");
        return;
      }
      this.youtubeAdding = true;
      try {
        const info = await this.fetchYoutubeVideoInfo(vid);
        if (!info) return;
        this.youtubeVideos.push({ vid, url, ...info });
        this.youtubeInput = "";
      } finally {
        this.youtubeAdding = false;
      }
    },
    removeYoutubeVideo(index) {
      this.youtubeVideos.splice(index, 1);
    },
    async fetchYoutubeVideoInfo(vid) {
      const apiKey = process.env.VUE_APP_YOUTUBE_API_KEY;
      try {
        const res = await axios.get(
          `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${vid}&key=${apiKey}`
        );
        const items = res.data.items;
        if (!items || items.length === 0) {
          this.toast("영상을 찾을 수 없습니다.", "danger");
          return null;
        }
        const snippet = items[0].snippet;
        return {
          title: snippet.title,
          channel: snippet.channelTitle,
          channelId: snippet.channelId,
        };
      } catch (err) {
        this.toast(`YouTube API 요청 실패: ${err.message}`, "danger");
        return null;
      }
    },

    validateLinkookSettlement() {
      if (this.purchaseType !== "linkook") return true;
      if (this.userStore.auth !== "admin") {
        this.toast("링꾹 구매는 관리자만 선택할 수 있습니다", "default");
        return false;
      }
      if (!this.settlementBank.trim()) {
        this.toast("정산 은행을 입력해주세요", "default");
        return false;
      }
      if (!this.settlementAccountNumber.trim()) {
        this.toast("정산 계좌번호를 입력해주세요", "default");
        return false;
      }
      if (!this.settlementAccountHolder.trim()) {
        this.toast("정산 예금주를 입력해주세요", "default");
        return false;
      }
      if (!this.sellerName.trim()) {
        this.toast("판매자명을 입력해주세요", "default");
        return false;
      }
      return true;
    },
    getPurchasePayloadFields() {
      const isLink = this.purchaseType === "link";
      const isLinkook = this.purchaseType === "linkook";
      return {
        basePrice: Number(this.basePrice) || 0,
        purchaseLink: isLink ? (this.purchaseLink?.trim() || "") : "",
        settlementBank: isLinkook ? this.settlementBank.trim() : "",
        settlementAccountNumber: isLinkook ? this.settlementAccountNumber.trim() : "",
        settlementAccountHolder: isLinkook ? this.settlementAccountHolder.trim() : "",
        sellerName: isLinkook ? this.sellerName.trim() : "",
      };
    },
    isValidBasePrice(value) {
      if (value === null || value === undefined || value === "") return false;
      const num = Number(value);
      return Number.isFinite(num) && num >= 0;
    },

    // Populate for update mode
    async populateForm(data) {
      this.ignorePurchaseTypeWatch = true;
      try {
        this.category = data.category || "";
        this.name = data.name || "";
        this.link = data.link || "";
        this.description = data.description || "";
        this.descriptionHtml = data.descriptionHtml || "";
        this.originalThumbnail = data.thumbnail || null;
        this.hashtags = Array.isArray(data.hashtags)
          ? data.hashtags.map(t => String(t).replace(/^#+/, "").replace(/\s+/g, "")).filter(Boolean).slice(0, this.maxHashtags)
          : [];
        this.hashtagInput = "";

        if (this.originalThumbnail) {
          this.thumbnailPreviewUrl = await this.getImagePreviewUrl("commission", this.originalThumbnail);
        }

        // Detail images
        const imgs = data.detailImages || [];
        for (let i = 0; i < 4; i++) {
          this.detailImages[i].original = imgs[i] || null;
          this.detailImages[i].file = null;
          if (imgs[i]) {
            this.detailImages[i].previewUrl = await this.getImagePreviewUrl("commission", imgs[i]);
          } else {
            this.detailImages[i].previewUrl = null;
          }
        }

        // YouTube
        this.youtubeVideos = Array.isArray(data.youtubeVideos) ? [...data.youtubeVideos] : [];

        // Purchase
        const purchaseTypeRaw = data.purchaseType || "link";
        this.purchaseType = purchaseTypeRaw === "link" ? "link" : "linkook";
        this.basePrice = (data.basePrice !== null && data.basePrice !== undefined && data.basePrice !== "")
          ? Number(data.basePrice)
          : null;
        this.purchaseLink = data.purchaseLink || "";
        this.settlementBank = data.settlementBank || "";
        this.settlementAccountNumber = data.settlementAccountNumber || "";
        this.settlementAccountHolder = data.settlementAccountHolder || "";
        this.sellerName = data.sellerName || "";
        this.status = data.status === "draft" ? "draft" : "published";
      } finally {
        this.$nextTick(() => {
          this.ignorePurchaseTypeWatch = false;
          this.syncDescriptionEditorFromModel();
        });
      }
    },

    initDescriptionEditor() {
      const el = this.$refs.descriptionEditorMount;
      if (!el || this._descriptionToastEditor) return;
      this._descriptionToastEditor = new Editor({
        el,
        height: "480px",
        initialEditType: "wysiwyg",
        initialValue: prepareToastViewerMarkdown(this.description || ""),
        usageStatistics: false,
        hideModeSwitch: true,
        language: "ko-KR",
        autofocus: false,
        placeholder: "예: 작업 소개, 작업 절차, 안내사항 등",
        extendedAutolinks: true,
        linkAttributes: { target: "_blank", rel: "noopener noreferrer" },
        plugins: [colorSyntax, tableMergedCell],
        toolbarItems: [
          ["heading", "bold", "italic", "strike"],
          ["hr", "quote"],
          ["ul", "ol", "task"],
          ["table", "code", "codeblock"],
          ["link", "image"],
        ],
        hooks: {
          addImageBlobHook: async (blob, callback) => {
            try {
              const url = await uploadToastImage(blob, "toastui");
              const altText = (blob && blob.name) ? blob.name : "image";
              callback(url, altText);
            } catch (err) {
              console.error("Toast UI 이미지 업로드 실패:", err);
              this.toast("이미지 업로드에 실패했습니다", "danger");
            }
          },
        },
      });
      this._descriptionToastEditor.on("change", () => {
        this.description = this._descriptionToastEditor.getMarkdown();
      });
    },
    destroyDescriptionEditor() {
      if (!this._descriptionToastEditor) return;
      try {
        this.description = this._descriptionToastEditor.getMarkdown();
      } catch (_) {
        /* noop */
      }
      try {
        this._descriptionToastEditor.destroy();
      } catch (_) {
        /* noop */
      }
      this._descriptionToastEditor = null;
    },
    syncDescriptionEditorFromModel() {
      if (!this._descriptionToastEditor) return;
      try {
        this._descriptionToastEditor.setMarkdown(prepareToastViewerMarkdown(this.description || ""));
      } catch (_) {
        /* noop */
      }
    },

    async getImagePreviewUrl(path, fileName) {
      if (!fileName) return null;
      if (fileName.startsWith("http")) return fileName;
      try {
        const imageRef = ref(storage, `${path}/${fileName}`);
        return await getDownloadURL(imageRef);
      } catch {
        return null;
      }
    },

    // Submit
    async handleSubmit() {
      this.status = "published";
      if (this.isUpdateMode) {
        await this.update();
      } else {
        await this.create();
      }
    },
    async handleSaveDraft() {
      if (!this.canSaveDraft) {
        this.toast("필수 항목(카테고리, 커미션명, 썸네일, 구매가격, 링꾹 구매 시 정산·판매자 정보)을 모두 입력해주세요", "default");
        return;
      }
      this.status = "draft";
      if (this.isUpdateMode) {
        await this.update();
      } else {
        await this.saveDraftCreate();
      }
    },
    mapYoutubePayload() {
      return this.youtubeVideos.map(v => ({
        vid: v.vid,
        url: v.url,
        title: v.title,
        channel: v.channel,
        channelId: v.channelId,
      }));
    },
    async saveDraftCreate() {
      this.link = this.normalizeUrlIfNoScheme(this.link);
      const detailFiles = this.detailImages.map(d => d.file);
      const commissionData = {
        category: this.category,
        name: this.name.trim(),
        link: this.link.trim(),
        description: this.description?.trim() || "",
        descriptionHtml: this.descriptionHtml?.trim() || "",
        thumbnail: "",
        hashtags: [...this.hashtags],
        _detailImageFiles: detailFiles,
        youtubeVideos: this.mapYoutubePayload(),
        purchaseType: this.purchaseType || "link",
        ...this.getPurchasePayloadFields(),
        createdBy: this.userStore.email,
        createdByNickname: this.userStore.nickname,
        createdAt: new Date(),
        status: "draft",
      };
      try {
        this.uploading = true;
        await createCommission(commissionData, this.thumbnailFile);
        this.toast("임시저장이 완료되었습니다!", "default");
        this.cancelPopup();
        this.renew();
      } catch (error) {
        this.toast("임시저장 중 오류가 발생했습니다", "danger");
        console.error("Error draft-saving commission:", error);
      } finally {
        this.uploading = false;
      }
    },
    async create() {
      if (!this.category) { this.toast("카테고리를 선택해주세요", "default"); return; }
      if (this.name.trim() === "") { this.toast("커미션명을 입력해주세요", "default"); return; }
      if (!this.thumbnailFile) { this.toast("썸네일을 선택해주세요", "default"); return; }
      if (this.link.trim()) {
        this.link = this.normalizeUrlIfNoScheme(this.link);
        if (!this.checkUrl(this.link)) { this.toast("올바른 URL 형식을 입력해주세요", "default"); return; }
      }
      if (!this.purchaseType) { this.toast("구매 방식을 선택해주세요", "default"); return; }
      if (!this.isValidBasePrice(this.basePrice)) { this.toast("구매가격(기본가)을 입력해주세요", "default"); return; }
      if (this.purchaseType === "link" && !this.purchaseLink.trim()) { this.toast("구매 링크를 입력해주세요", "default"); return; }
      if (this.purchaseType === "linkook" && this.userStore.auth !== "admin") {
        this.toast("링꾹 구매는 관리자만 선택할 수 있습니다", "default");
        return;
      }
      if (!this.validateLinkookSettlement()) return;

      const detailFiles = this.detailImages.map(d => d.file);

      const commissionData = {
        category: this.category,
        name: this.name.trim(),
        link: this.link.trim(),
        description: this.description?.trim() || "",
        descriptionHtml: this.descriptionHtml?.trim() || "",
        thumbnail: "",
        hashtags: [...this.hashtags],
        _detailImageFiles: detailFiles,
        youtubeVideos: this.mapYoutubePayload(),
        purchaseType: this.purchaseType,
        ...this.getPurchasePayloadFields(),
        createdBy: this.userStore.email,
        createdByNickname: this.userStore.nickname,
        createdAt: new Date(),
        status: this.status,
      };

      try {
        this.uploading = true;
        await createCommission(commissionData, this.thumbnailFile);
        this.cancelPopup();
        this.toast("업로드완료! 승인을 기다립니다", "default");
        this.renew();
      } catch (error) {
        this.toast("등록 중 에러가 발생했습니다", "danger");
        console.error("Error creating commission:", error);
      } finally {
        this.uploading = false;
      }
    },
    async update() {
      if (this.name.trim() === "") { this.toast("커미션명을 입력해주세요", "default"); return; }
      if (this.link.trim()) {
        this.link = this.normalizeUrlIfNoScheme(this.link);
        if (!this.checkUrl(this.link)) { this.toast("올바른 URL 형식을 입력해주세요", "default"); return; }
      }
      if (!this.isValidBasePrice(this.basePrice)) {
        this.toast("구매가격(기본가)을 입력해주세요", "default");
        return;
      }
      if (this.purchaseType === "link" && !this.purchaseLink.trim()) {
        this.toast("구매 링크를 입력해주세요", "default");
        return;
      }
      if (this.purchaseType === "linkook" && this.userStore.auth !== "admin") {
        this.toast("링꾹 구매는 관리자만 선택할 수 있습니다", "default");
        return;
      }
      if (!this.validateLinkookSettlement()) return;

      const detailFiles = this.detailImages.map(d => d.file);

      const updateData = {
        category: this.category,
        name: this.name.trim(),
        link: this.link.trim(),
        description: this.description?.trim() || "",
        descriptionHtml: this.descriptionHtml?.trim() || "",
        thumbnail: this.originalThumbnail || "",
        hashtags: [...this.hashtags],
        detailImages: this.detailImages.map(d => d.original || ""),
        _detailImageFiles: detailFiles,
        youtubeVideos: this.mapYoutubePayload(),
        purchaseType: this.purchaseType,
        ...this.getPurchasePayloadFields(),
        lastModifiedBy: this.userStore.email,
        lastModifiedByNickname: this.userStore.nickname,
        lastModifiedAt: new Date(),
        status: this.status,
      };

      const wasDraft = (this.commissionData.status || "published") === "draft";
      if (wasDraft && this.status === "published") {
        updateData.approved = false;
      }

      try {
        this.uploading = true;
        await updateCommission(this.commissionData.id, updateData, this.thumbnailFile);
        this.cancelPopup();
        if (this.status === "draft") {
          this.toast("임시저장이 완료되었습니다!", "default");
        } else {
          this.toast("수정이 완료되었습니다!", "default");
        }
        this.renew();
      } catch (error) {
        this.toast("수정 중 에러가 발생했습니다", "danger");
        console.error("Error updating commission:", error);
      } finally {
        this.uploading = false;
      }
    },

    cancelPopup() {
      this.$emit("close-popup");
    },
    renew() {
      this.currentStep = 1;
      this.category = "";
      this.name = "";
      this.link = "";
      this.description = "";
      this.descriptionHtml = "";
      this.thumbnailFile = null;
      this.originalThumbnail = null;
      this.thumbnailPreviewUrl = null;
      this.hashtags = [];
      this.hashtagInput = "";
      this.detailImages = [
        { file: null, original: null, previewUrl: null },
        { file: null, original: null, previewUrl: null },
        { file: null, original: null, previewUrl: null },
        { file: null, original: null, previewUrl: null },
      ];
      this.youtubeVideos = [];
      this.youtubeInput = "";
      this.youtubeAdding = false;
      this.purchaseType = "link";
      this.basePrice = null;
      this.purchaseLink = "";
      this.settlementBank = "";
      this.settlementAccountNumber = "";
      this.settlementAccountHolder = "";
      this.sellerName = "";
      this.status = "published";
    },
    normalizeUrlIfNoScheme(value) {
      const t = String(value || "").trim();
      if (!t) return "";
      if (/^https?:\/\//i.test(t)) return t;
      return `https://${t}`;
    },
    checkUrl(url) {
      const u = this.normalizeUrlIfNoScheme(url);
      if (!u) return false;
      const urlPattern = /^https?:\/\/[^\s$.?#].[^\s]*$/i;
      return urlPattern.test(u);
    },
    toast(message, type) {
      createToast(message, {
        type: type,
        timeout: 3000,
        position: "top-center",
      });
    },
  },
};
</script>

<style module>
/* Step Indicator */
.stepIndicator {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
  padding: 0 10px;
  gap: 10px;
}

.stepItem {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
}

.stepItem::after {
  content: "";
  position: absolute;
  top: 16px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: #e0e0e0;
  z-index: -1;
}

.stepItem:last-child::after {
  display: none;
}

.stepItem.completed::after {
  background: #66bdff;
}

.stepNumber {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f5f5f5;
  border: 2px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #999;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.3s ease;
}

.stepItem.active .stepNumber {
  background: #66bdff;
  border-color: #66bdff;
  color: #fff;
}

.stepItem.completed .stepNumber {
  background: #66bdff;
  border-color: #66bdff;
  color: #fff;
}

.stepLabel {
  font-size: 12px;
  color: #999;
  font-family: "NanumSquareRoundR", sans-serif;
  text-align: center;
  white-space: nowrap;
}

.stepItem.active .stepLabel {
  color: #66bdff;
  font-weight: 600;
}

.stepItem.completed .stepLabel {
  color: #66bdff;
}

/* Typography */
h4 {
  font-family: "Paperlogy7", sans-serif;
  font-size: 14px;
  margin: 0 0 8px 0;
  color: #444;
  font-weight: 600;
  position: relative;
  text-align: left;
}

h4 span {
  font-size: 12px;
  font-weight: 400;
  color: #999;
  margin-left: 4px;
}

.required::after {
  content: "*";
  color: #ff4444;
  font-size: 12px;
  font-weight: bold;
  margin-left: 4px;
  vertical-align: top;
  line-height: 1;
}

/* Inputs */
input {
  height: 40px;
  width: 100%;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
}

input[type="radio" i] {
  display: none;
}

.textInput {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 14px;
  transition: border-color 0.15s ease;
  background: #fff;
  color: #333;
  box-sizing: border-box;
}

.textInput::placeholder {
  color: #999;
}

.textInput:focus {
  outline: none;
  border-color: #66bdff;
}

.textInput:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
  border-color: #e0e0e0;
}

.descriptionEditorHost {
  width: 100%;
  text-align: left;
}

.tinyEditorHost {
  width: 100%;
  text-align: left;
}

.tinyEditorHost :global(.tox-tinymce) {
  border: 1px solid #ddd;
  border-radius: 6px;
}

/* 모달(z-index 9999) 위로 TinyMCE 메뉴/드롭다운/대화상자가 올라오도록 */
:global(.tox.tox-tinymce-aux),
:global(.tox-silver-sink) {
  z-index: 100000 !important;
}

:global(.tox-dialog-wrap) {
  z-index: 100001 !important;
}

/* overflow:hidden 이면 이미지/링크 팝업(absolute, defaultUI 자식)이 잘림 */
.descriptionEditorHost :global(.toastui-editor-defaultUI) {
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: visible;
}

/* 이미지 첨부 팝업: 하단 버튼을 가리지 않도록 화면 중앙에 띄움(크기는 기본값) */
.descriptionEditorHost :global(.toastui-editor-popup.toastui-editor-popup-add-image) {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  z-index: 2000;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}

/* Toast UI: 툴바·아이콘을 모달에 맞게 축소 */
.descriptionEditorHost :global(.toastui-editor-defaultUI-toolbar) {
  height: 36px;
  padding: 0 8px;
  align-items: center;
}

.descriptionEditorHost :global(.toastui-editor-toolbar) {
  height: 36px;
  box-sizing: border-box;
}

.descriptionEditorHost :global(.toastui-editor-defaultUI-toolbar button) {
  width: 26px;
  height: 26px;
  margin: 4px 2px;
  transform: scale(0.78);
  transform-origin: center center;
}

.descriptionEditorHost :global(.toastui-editor-toolbar-divider) {
  height: 14px;
  margin: 10px 6px;
}

.descriptionEditorHost :global(.toastui-editor-toolbar-item-wrapper) {
  height: 26px;
  line-height: 26px;
}

.textareaInput {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 14px;
  resize: vertical;
  min-height: 90px;
  box-sizing: border-box;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: border-color 0.15s ease;
}

.textareaInput:focus {
  border-color: #66bdff;
  outline: none;
}

.input {
  padding: 0;
  margin-bottom: 20px;
}

/* Radio buttons (LiveForm style) */
.radio {
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.radio input[type="radio"] {
  display: none;
}

.radio label {
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  padding: 10px 20px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #666;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;
  font-weight: 400;
  position: relative;
  display: block;
  flex: 1;
  min-width: 80px;
}

.radio label:hover {
  border-color: #66bdff;
  color: #66bdff;
}

.radio label:has(input[type="radio"]:checked) {
  border-color: #66bdff;
  background: #66bdff;
  color: #fff;
  font-weight: 500;
}

/* File input (LiveForm style) */
.fileInputLabel {
  display: block;
  cursor: pointer;
}

.fileInputHidden {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
}

.fileInputButton {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 20px;
  border: 1px solid #66bdff;
  border-radius: 6px;
  background: #f8f9ff;
  color: #66bdff;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.15s ease;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
}

.fileInputButton:hover {
  border-color: #4a9eff;
  background: #f0f7ff;
  color: #4a9eff;
}

.fileInputButton span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.fileInputLabel:has(.fileInputHidden:disabled) .fileInputButton {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f5f5f5;
}

/* Image preview */
.imagePreviewContainer {
  margin-bottom: 10px;
}

.imagePreview {
  position: relative;
  display: inline-block;
  max-width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f5f5f5;
}

.previewImage {
  max-width: 100%;
  max-height: 300px;
  display: block;
  object-fit: contain;
}

.deleteImageButton {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 0, 0, 0.75);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
}

.deleteImageButton:hover {
  background: rgba(255, 0, 0, 0.9);
}

.newFileInfo {
  margin-top: 5px;
  font-size: 12px;
  color: #666;
  font-style: italic;
}

/* LiveForm .infoText 와 동일 (링꾹 구매 안내) */
.infoText {
  font-size: 12px;
  color: #66bdff;
  margin: 8px 0;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.6;
  padding: 8px 12px;
  background: #f0f7ff;
  border-radius: 4px;
  border-left: 3px solid #66bdff;
}

/* 카테고리 — VideoForm 동영상 타입과 동일 패턴 */
.typeSelector {
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.typeButton {
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  padding: 10px 20px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #666;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;
  font-weight: 400;
  flex: 1;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.typeButton:hover:not(:disabled) {
  border-color: #66bdff;
  color: #66bdff;
}

.typeButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.typeButtonActive {
  border-color: #66bdff;
  background: #66bdff;
  color: #fff;
  font-weight: 500;
}

.typeButtonActive .typeLabel {
  color: #fff;
}

.typeLabel {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 14px;
  color: inherit;
  transition: color 0.15s ease;
}

/* 해시태그 섹션 */
.hashtagInputRow {
  display: flex;
  gap: 8px;
  align-items: center;
}

.hashtagInputRow .textInput {
  flex: 1;
  min-width: 0;
}

.hashtagList {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.hashtagChip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px 4px 10px;
  border-radius: 999px;
  background: rgba(102, 189, 255, 0.12);
  border: 1px solid rgba(102, 189, 255, 0.35);
  color: #3b8ec8;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 12px;
  line-height: 1;
}

.hashtagText {
  letter-spacing: 0.2px;
}

.hashtagRemove {
  width: 18px;
  height: 18px;
  line-height: 18px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(102, 189, 255, 0.18);
  color: #3b8ec8;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}

.hashtagRemove:hover:not(:disabled) {
  background: #e74c3c;
  color: #fff;
}

.hashtagRemove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* YouTube section */
.youtubeInputRow {
  display: flex;
  gap: 8px;
  align-items: center;
}

.youtubeInputRow .textInput {
  flex: 1;
  min-width: 0;
}

.addButton {
  padding: 10px 20px;
  border-radius: 6px;
  background: #66bdff;
  color: #fff;
  border: none;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: background 0.15s ease;
  white-space: nowrap;
  height: 42px;
}

.addButton:hover:not(:disabled) {
  background: #4a9eff;
}

.addButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.youtubeVideoList {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
}

.youtubeVideoItem {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 10px;
}

.youtubeVideoThumb {
  width: 100px;
  height: 56px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
  background: #ddd;
}

.youtubeVideoInfo {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.youtubeVideoTitle {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  color: #222;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.youtubeVideoChannel {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 12px;
  color: #868e96;
}

.removeButton {
  flex-shrink: 0;
  background: #fff;
  color: #e74c3c;
  border: 1px solid #e74c3c;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.15s;
}

.removeButton:hover:not(:disabled) {
  background: #e74c3c;
  color: #fff;
}

.removeButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Buttons — LiveForm.vue와 동일 */
.buttons {
  margin-top: 24px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.cancel {
  padding: 12px 24px;
  border-radius: 6px;
  background: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.cancel:hover:not(:disabled) {
  background: #e8e8e8;
  border-color: #ccc;
}

.cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.saveDraft {
  padding: 12px 24px;
  border-radius: 6px;
  background: #f8f9fa;
  color: #666;
  border: 1px solid #ddd;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.saveDraft:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #adb5bd;
  color: #495057;
}

.saveDraft:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.prevButton {
  padding: 12px 24px;
  border-radius: 6px;
  background: #f8f9fa;
  color: #666;
  border: 1px solid #ddd;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.prevButton:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #adb5bd;
  color: #495057;
}

.prevButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.nextButton {
  padding: 12px 24px;
  border-radius: 6px;
  background: #66bdff;
  color: #fff;
  border: none;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.nextButton:hover:not(:disabled) {
  background: #4a9eff;
}

.nextButton:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.register {
  padding: 12px 24px;
  border-radius: 6px;
  background: #66bdff;
  color: #fff;
  border: none;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.register:hover:not(:disabled) {
  background: #4a9eff;
}

.register:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 15px;
  height: 15px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  box-sizing: border-box;
  vertical-align: middle;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* Mobile — LiveForm.vue 스텝 인디케이터·버튼 영역과 동일 */
@media (max-width: 480px) {
  .stepIndicator {
    margin-bottom: 24px;
    padding: 0 5px;
    gap: 5px;
  }

  .stepNumber {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }

  .stepLabel {
    font-size: 10px;
  }

  .stepItem::after {
    top: 14px;
  }

  .infoText {
    font-size: 11px;
    padding: 6px 10px;
  }

  .buttons {
    margin-top: 20px;
    flex-wrap: wrap;
    gap: 8px;
  }

  .cancel {
    width: 100%;
    padding: 12px;
    font-size: 14px;
    order: 4;
  }

  .saveDraft {
    width: 100%;
    padding: 12px;
    font-size: 14px;
    order: 3;
  }

  .prevButton {
    flex: 1;
    padding: 12px;
    font-size: 14px;
    order: 1;
  }

  .nextButton,
  .register {
    flex: 1;
    padding: 12px;
    font-size: 14px;
    order: 2;
  }

  .spinner {
    width: 14px;
    height: 14px;
    margin-right: 6px;
  }

  .youtubeInputRow {
    flex-wrap: nowrap;
  }

  .youtubeVideoItem {
    gap: 8px;
    padding: 8px;
  }

  .youtubeVideoThumb {
    width: 80px;
    height: 45px;
  }

  .youtubeVideoTitle {
    font-size: 12px;
  }

  .typeSelector {
    gap: 6px;
  }

  .typeButton {
    font-size: 13px;
    padding: 8px 16px;
    min-width: 70px;
  }

  .typeLabel {
    font-size: 13px;
  }
}
</style>
